/**
 * ListViewGrid: plantilla estándar para listas en vista cuadrícula (grid).
 * Misma barra de filtros (sin "Editar columnas"), paginación 25/50/100 y botón principal con icono +.
 * Usar en páginas que muestran recursos en tarjetas (ej. Contenido, galerías).
 * Regla: la barra de filtros debe construirse con showEditColumnsButton: false.
 */

import { useMemo, type ReactNode } from 'react';
import { useStoreNavigate } from '@/hooks/useStoreNavigate';
import {
  Page,
  Card,
  Text,
  Box,
  Grid,
  EmptyState,
  InlineStack,
  Select,
  Pagination,
  BlockStack,
} from '@/lib/polaris';
import { PlusIcon } from '@/lib/polaris-icons';
import { PageBreadcrumbTitle } from '@/components/polaris/PageBreadcrumbTitle';
import { buildListViewPagination, LIST_PAGE_SIZE_OPTIONS } from '@/components/admin/ListView';
import type { ListViewConfig } from '@/types/listView.types';
import type { UsePaginationReturn } from '@smarty/shared';

const GRID_COLUMNS = { xs: 2, sm: 3, md: 4, lg: 5 } as const;
const SKELETON_CELL_COUNT = 10;

function GridSkeletonCard() {
  return (
    <Card>
      <BlockStack gap="200">
        <Box
          minBlockSize="140px"
          background="bg-surface-secondary"
          borderRadius="200"
          overflow="hidden"
        />
        <Box paddingBlockStart="100">
          <Box background="bg-surface-secondary" minBlockSize="16px" borderRadius="100" />
          <Box
            paddingBlockStart="100"
            background="bg-surface-secondary"
            minBlockSize="12px"
            borderRadius="100"
            maxInlineSize="60%"
          />
        </Box>
      </BlockStack>
    </Card>
  );
}

export interface ListViewGridProps<Item extends object> {
  /** Configuración de la lista (título, empty state, primary action, resource name). */
  config: ListViewConfig<Item>;
  /** Nodo del título (breadcrumb). Si no se pasa, se construye desde config. */
  pageTitle?: ReactNode;
  /** Acción primaria del Page. Si no se pasa, se construye desde config.primaryAction con icono +. */
  primaryAction?: {
    content: string;
    onAction: () => void;
    icon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
    loading?: boolean;
  };
  /** Acciones secundarias (ej. toggle vista lista/cuadrícula). */
  secondaryActions?: Array<{ content: string; icon?: React.ComponentType<React.SVGProps<SVGSVGElement>>; onAction: () => void }>;
  /** Barra de filtros (IndexFilters). Debe construirse con showEditColumnsButton: false. */
  filterControl: ReactNode;
  /** Cargando datos. */
  loading: boolean;
  /** Items ya filtrados y paginados a mostrar en la cuadrícula. */
  items: Item[];
  /** Obtener id del item (para key y paginación). */
  getItemId: (item: Item) => string;
  /** Render de cada tarjeta. */
  renderCard: (item: Item) => ReactNode;
  /** Paginación (mismo hook que ListView). */
  pagination: UsePaginationReturn;
  /** Descripción del recurso en plural para "Mostrando X–Y de Z". Si no se pasa, se usa config.resourceName.plural. */
  resourceNamePlural?: string;
}

/**
 * Plantilla de vista en cuadrícula. Botón principal con icono + por defecto; paginación estándar 25/50/100.
 * La barra de filtros no muestra "Editar columnas" (construir con showEditColumnsButton: false).
 */
export function ListViewGrid<Item extends object>({
  config,
  pageTitle,
  primaryAction: primaryActionProp,
  secondaryActions,
  filterControl,
  loading,
  items,
  getItemId,
  renderCard,
  pagination,
  resourceNamePlural: resourceNamePluralProp,
}: ListViewGridProps<Item>) {
  const storeNavigate = useStoreNavigate();
  const resourceNamePlural = resourceNamePluralProp ?? config.resourceName?.plural ?? '';

  const pageTitleNode = useMemo(
    () =>
      pageTitle ?? (
        <PageBreadcrumbTitle
          icon={config.titleIcon}
          title={config.title}
          onBack={config.onBack}
          backAccessibilityLabel={config.backAccessibilityLabel ?? 'Volver al inicio'}
        />
      ),
    [pageTitle, config.titleIcon, config.title, config.onBack, config.backAccessibilityLabel]
  );

  const primaryAction = useMemo(() => {
    if (primaryActionProp) {
      return {
        ...primaryActionProp,
        icon: primaryActionProp.icon ?? PlusIcon,
      };
    }
    const action = config.primaryAction;
    if (!action) return undefined;
    // Mismo motivo que en ListView: `window.location` tira la app entera. Ver el comentario ahí.
    const onAction = action.onAction ?? (action.to ? () => { storeNavigate(action.to!); } : undefined);
    if (!onAction) return undefined;
    const defaultContent =
      config.resourceName?.singular != null ? `Agregar ${config.resourceName.singular}` : (action.content ?? '');
    return {
      content: action.content ?? defaultContent,
      onAction,
      icon: action.icon ?? PlusIcon,
      loading: false,
    };
  }, [config.primaryAction, config.resourceName?.singular, primaryActionProp, storeNavigate]);

  const paginationOption = useMemo(() => buildListViewPagination(pagination), [pagination]);
  const showPagination =
    paginationOption != null &&
    items.length > 0 &&
    paginationOption.total > 0 &&
    paginationOption.total > paginationOption.pageSize;

  const paginationBar = useMemo(
    () =>
      showPagination && paginationOption ? (
        <Box padding="400" borderBlockStartWidth="025" borderColor="border-secondary">
          <InlineStack align="space-between">
            <Text variant="bodyMd" tone="subdued" as="p">
              Mostrando {paginationOption.from}-{paginationOption.to} de {paginationOption.total} {resourceNamePlural}
            </Text>
            <InlineStack gap="200" align="center">
              <Select
                label=""
                labelHidden
                options={paginationOption.pageSizeOptions ?? LIST_PAGE_SIZE_OPTIONS}
                value={String(paginationOption.pageSize)}
                onChange={(value) => paginationOption.onPageSizeChange(Number(value))}
              />
              <Pagination
                label={paginationOption.label ?? `Página ${paginationOption.totalPages}`}
                hasPrevious={paginationOption.hasPrevious}
                onPrevious={paginationOption.onPrevious}
                hasNext={paginationOption.hasNext}
                onNext={paginationOption.onNext}
              />
            </InlineStack>
          </InlineStack>
        </Box>
      ) : null,
    [showPagination, paginationOption, resourceNamePlural]
  );

  return (
    <Page
      title={pageTitleNode}
      primaryAction={primaryAction}
      secondaryActions={secondaryActions}
    >
      {loading ? (
        <Card padding="0">
          <Box padding="400">
            <Grid columns={GRID_COLUMNS}>
              {Array.from({ length: SKELETON_CELL_COUNT }, (_, i) => (
                <Grid.Cell key={i} columnSpan={{ xs: 1, sm: 1, md: 1, lg: 1 }}>
                  <GridSkeletonCard />
                </Grid.Cell>
              ))}
            </Grid>
          </Box>
        </Card>
      ) : (
        <BlockStack gap="400">
          <Card padding="0">{filterControl}</Card>
          {items.length === 0 ? (
            <Card>
              <EmptyState
                heading={config.emptyState.heading}
                action={(() => {
                  const action = config.emptyState.action;
                  if (!action) return undefined;
                  const onAction =
                    action.onAction ?? (action.to ? () => storeNavigate(action.to) : undefined);
                  return {
                    content: action.content ?? `Agregar ${config.resourceName.singular}`,
                    onAction,
                  };
                })()}
                image={config.emptyState.image}
              >
                {config.emptyState.description ? (
                  <Text as="p" variant="bodyMd">
                    {config.emptyState.description}
                  </Text>
                ) : null}
              </EmptyState>
            </Card>
          ) : (
            <Card padding="0">
              <Box padding="400">
                <Grid columns={GRID_COLUMNS}>
                  {items.map((item) => (
                    <Grid.Cell key={getItemId(item)} columnSpan={{ xs: 1, sm: 1, md: 1, lg: 1 }}>
                      {renderCard(item)}
                    </Grid.Cell>
                  ))}
                </Grid>
              </Box>
              {paginationBar}
            </Card>
          )}
        </BlockStack>
      )}
    </Page>
  );
}
